import { useMemo, useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import ForecastSettings, { type ForecastConfig } from '../components/ForecastSettings'
import Tooltip from '../components/Tooltip'
import { continueForecast, startForecast } from '../api/client'
import { useForecastStore } from '../store/forecastStore'
import { useSSE } from '../hooks/useSSE'
import { useNavigate } from 'react-router-dom'

const DEFAULT_CONFIG: ForecastConfig = {
  formatter_model: 'gpt-5-nano',
  predictor_model: 'gpt-5-nano',
  supervisor_model: 'gpt-5-mini',
  search_depth: 'low',
  predictor_reasoning_effort: 'low',
  supervisor_reasoning_effort: 'medium',
  num_agents: 10,
  agent_configs: null,
}

type SuggestedCategoryKey = 'uk-politics' | 'geopolitics' | 'economics' | 'ai-tech' | 'sport'

const SUGGESTED_CATEGORIES: Array<{ key: SuggestedCategoryKey; label: string; questions: string[] }> = [
  {
    key: 'uk-politics',
    label: 'UK politics',
    questions: [
      'Will Sir Keir Starmer be UK Prime Minister on 31 Dec 2026?',
      'Will there be a UK general election in 2026 or 2027 (polling day after 31 Dec 2025 and before 31 Dec 2027)?',
      'Will Reform UK have ≥10 MPs on 31 Dec 2026?',
      'Will the UK Government pass legislation creating a new GB Energy public company by 31 Dec 2026?',
      'Will there be a change of Chancellor of the Exchequer at any point in 2026?',
    ],
  },
  {
    key: 'geopolitics',
    label: 'Geopolitics',
    questions: [
      'Will Russia and Ukraine agree a ceasefire that lasts more than 30 days at any point in 2026?',
      'Will Russia conduct a new mobilisation (partial or full) in 2026?',
      'Will Iran conduct a declared nuclear weapons test by 31 Dec 2026?',
      'Will North Korea conduct an ICBM test in 2026?',
      'Will China impose a sustained blockade-like operation around Taiwan lasting ≥7 consecutive days in 2026?',
      'Will any new country join NATO in 2026?',
      'Will any new country join the EU in 2026?',
    ],
  },
  {
    key: 'economics',
    label: 'Economics',
    questions: [
      'Will UK CPI inflation (12-month) be below 3.0% in Dec 2026?',
      'Will the UK have a technical recession in 2026 (two consecutive negative GDP quarters)?',
      'Will UK real GDP growth in 2026 be above 1.0% (annual)?',
      'Will Brent crude average below $70 in 2026?',
      'Will Bitcoin trade above $150,000 at any point by 31 Dec 2026?',
    ],
  },
  {
    key: 'ai-tech',
    label: 'AI & tech',
    questions: [
      'Will OpenAI release a new flagship frontier model (GPT-6) by 31 Dec 2026?',
      'Will NVIDIA’s market cap exceed $5 trillion at any point by 31 Dec 2026?',
      'Will the EU issue its first enforcement fine under the AI Act by 31 Dec 2026?',
      'Will an open-weights model match or exceed the leading closed model on a major public leaderboard in Dec 2026?',
    ],
  },
  {
    key: 'sport',
    label: 'Sport',
    questions: [
      'Will Norway win the most gold medals at the Milano Cortina 2026 Winter Olympics?',
      'Will England reach the World Cup 2026 final?',
      'Will Ireland win the Six Nations 2026?',
    ],
  },
]

const ALL_SUGGESTED_QUESTIONS = SUGGESTED_CATEGORIES.flatMap((c) => c.questions)

export default function HomePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [question, setQuestion] = useState('')
  const [config, setConfig] = useState<ForecastConfig>(DEFAULT_CONFIG)
  const [isLoading, setIsLoading] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showCustomQuestion, setShowCustomQuestion] = useState(false)
  const [customQuestion, setCustomQuestion] = useState('')
  const [customCriteria, setCustomCriteria] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [editableFormatted, setEditableFormatted] = useState({
    question: '',
    criteria: '',
    endDate: '',
  })
  const [typewriterText, setTypewriterText] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const charIndexRef = useRef(0)
  const isDeletingRef = useRef(false)
  const currentQuestionIndexRef = useRef<number>(-1)
  const currentTypewriterQuestionRef = useRef<string>(ALL_SUGGESTED_QUESTIONS[0] || '')
  const [activeCategory, setActiveCategory] = useState<SuggestedCategoryKey>('uk-politics')

  const currentJobId = useForecastStore((state) => state.currentJobId)
  const setCurrentJobId = useForecastStore((state) => state.setCurrentJobId)
  const currentQuestionId = useForecastStore((state) => state.currentQuestionId)
  const formatterStatus = useForecastStore((state) => state.formatterStatus)
  const formatterPreview = useForecastStore((state) => state.formatterPreview)
  const formatterSearch = useForecastStore((state) => state.formatterSearch)
  const setFormatterStatus = useForecastStore((state) => state.setFormatterStatus)
  const setFormatterPreview = useForecastStore((state) => state.setFormatterPreview)
  const setFormatterSearch = useForecastStore((state) => state.setFormatterSearch)

  // Connect to SSE for real-time updates
  useSSE(currentJobId)

  // Reset formatter state when navigating back to home page (if no active forecast)
  useEffect(() => {
    // Reset formatter state when on home page and there's no active job
    // This ensures the formatter panel is cleared when returning from results page
    if (location.pathname === '/' && !currentJobId && formatterStatus !== 'idle') {
      setFormatterStatus('idle')
      setFormatterPreview(null)
      setFormatterSearch({ status: 'idle' })
    }
  }, [location.pathname, currentJobId, formatterStatus, setFormatterStatus, setFormatterPreview, setFormatterSearch])

  // Typewriter effect for suggested questions
  useEffect(() => {
    // Only run typewriter effect when textarea is empty and not focused.
    // Note: formatterStatus can change while the effect is running, so we check that dynamically inside the timer loop.
    const shouldRun = question.trim() === '' && !isFocused
    
    if (!shouldRun) {
      setTypewriterText('')
      // Reset refs when effect stops
      charIndexRef.current = 0
      isDeletingRef.current = false
      return
    }

    const typeSpeed = 50 // milliseconds per character
    const deleteSpeed = 30 // milliseconds per character when deleting
    const pauseTime = 2000 // pause time after completing a question

    // Reset refs when effect starts
    charIndexRef.current = 0
    isDeletingRef.current = false
    // Pick an initial random question
    if (ALL_SUGGESTED_QUESTIONS.length > 0) {
      const first = Math.floor(Math.random() * ALL_SUGGESTED_QUESTIONS.length)
      currentQuestionIndexRef.current = first
      currentTypewriterQuestionRef.current = ALL_SUGGESTED_QUESTIONS[first]
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let isActive = true

    const typeWriter = () => {
      // Check if effect should still run
      if (question.trim() !== '' || isFocused || formatterStatus === 'reformatting' || !isActive) {
        return
      }

      const currentQuestion = currentTypewriterQuestionRef.current || ''

      if (isDeletingRef.current) {
        // Deleting characters
        if (charIndexRef.current > 0) {
          setTypewriterText(currentQuestion.substring(0, charIndexRef.current - 1))
          charIndexRef.current--
          timeoutId = setTimeout(typeWriter, deleteSpeed)
        } else {
          // Finished deleting, move to next question
          isDeletingRef.current = false
          if (ALL_SUGGESTED_QUESTIONS.length > 0) {
            let next = Math.floor(Math.random() * ALL_SUGGESTED_QUESTIONS.length)
            if (ALL_SUGGESTED_QUESTIONS.length > 1) {
              while (next === currentQuestionIndexRef.current) {
                next = Math.floor(Math.random() * ALL_SUGGESTED_QUESTIONS.length)
              }
            }
            currentQuestionIndexRef.current = next
            currentTypewriterQuestionRef.current = ALL_SUGGESTED_QUESTIONS[next]
          }
          timeoutId = setTimeout(typeWriter, pauseTime)
        }
      } else {
        // Typing characters
        if (charIndexRef.current < currentQuestion.length) {
          setTypewriterText(currentQuestion.substring(0, charIndexRef.current + 1))
          charIndexRef.current++
          timeoutId = setTimeout(typeWriter, typeSpeed)
        } else {
          // Finished typing, pause then start deleting
          timeoutId = setTimeout(() => {
            isDeletingRef.current = true
            typeWriter()
          }, pauseTime)
        }
      }
    }

    // Start the typewriter effect immediately
    typeWriter()

    return () => {
      isActive = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [question, isFocused, formatterStatus])

  const formatted = useMemo(() => {
    const output = formatterPreview?.output
    const base = {
      binary_question: output?.binary_question || '',
      resolution_criteria: output?.resolution_criteria || '',
      end_date: output?.end_date || '',
      is_valid: output?.is_valid,
      error_message: (output as any)?.error_message || '',
      validation_message: (output as any)?.validation_message || '',
    }
    // Initialize editable state when formatter preview changes
    if (output && !editableFormatted.question) {
      setEditableFormatted({
        question: base.binary_question,
        criteria: base.resolution_criteria,
        endDate: base.end_date,
      })
    }
    return base
  }, [formatterPreview, editableFormatted.question])

  const handleStartForecast = async (questionOverride?: string) => {
    const q = (questionOverride ?? question).trim()
    if (!q) {
      alert('Please enter a question')
      return
    }

    setIsLoading(true)
    try {
      // Reset inline formatter UI state
      setFormatterStatus('reformatting')
      setFormatterPreview(null)
      setFormatterSearch({ status: 'idle' })

      const response = await startForecast({
        question: q,
        ...config,
        agent_configs: config.agent_configs || undefined,
        skip_preview: false,
      })

      setCurrentJobId(response.job_id)
    } catch (error) {
      console.error('Failed to start forecast:', error)
      alert('Failed to start forecast: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuggestedQuestionPick = (q: string) => {
    setQuestion(q)
  }

  const handleEditQuestion = () => {
    setFormatterStatus('idle')
    setFormatterPreview(null)
    setFormatterSearch({ status: 'idle' })
    setCurrentJobId(null)
    setEditableFormatted({ question: '', criteria: '', endDate: '' })
  }

  const handleStartCustomForecast = async () => {
    if (!customQuestion.trim()) {
      alert('Please enter a question')
      return
    }

    if (!customCriteria.trim()) {
      alert('Please enter resolution criteria')
      return
    }

    setIsLoading(true)
    try {
      const response = await startForecast({
        question: customQuestion.trim(),
        formatted_question: customQuestion.trim(),
        resolution_criteria: customCriteria.trim(),
        end_date: customEndDate || null,
        ...config,
        agent_configs: config.agent_configs || undefined,
        skip_formatting: true, // Skip formatter stage entirely
        skip_preview: true, // Skip preview pause
      })

      setCurrentJobId(response.job_id)
      const qid = response.question_id
      if (qid) {
        // Navigate immediately - forecast is already running in background
        navigate(`/results/${qid}`)
      } else {
        // If no question_id yet, wait a bit for it to be created, then navigate
        // The SSE connection will handle updates
        setTimeout(() => {
          if (response.job_id) {
            // Try to get question_id from store or navigate anyway
            navigate(`/results`)
          }
        }, 500)
      }
    } catch (error) {
      console.error('Failed to start forecast:', error)
      alert('Failed to start forecast: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRunForecastWithEdits = async () => {
    // Use edited values if available, otherwise use original formatted values
    const questionText = editableFormatted.question.trim() || formatted.binary_question.trim()
    const criteriaText = editableFormatted.criteria.trim() || formatted.resolution_criteria.trim() || null
    const endDateValue = editableFormatted.endDate || formatted.end_date || null

    if (!questionText) {
      alert('Please enter a question')
      return
    }

    setIsLoading(true)
    try {
      // If we have a currentJobId from formatter, continue it (user may have edited)
      if (currentJobId && formatterPreview?.question_id) {
        const qid = currentQuestionId || formatterPreview.question_id
        if (qid) {
          navigate(`/results/${qid}`)
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => resolve(), 1500)
            const handler = (e: Event) => {
              const ce = e as CustomEvent
              if (ce.detail?.jobId === currentJobId) {
                clearTimeout(timeout)
                window.removeEventListener('sse_connected', handler as EventListener)
                resolve()
              }
            }
            window.addEventListener('sse_connected', handler as EventListener)
          })
          await continueForecast(currentJobId)
        }
      } else {
        // Start new forecast with edited question (skipping formatter)
        const response = await startForecast({
          question: questionText,
          formatted_question: questionText,
          resolution_criteria: criteriaText,
          end_date: endDateValue,
          ...config,
          agent_configs: config.agent_configs || undefined,
          skip_formatting: true, // Skip formatter stage entirely
          skip_preview: true, // Skip preview pause
        })

        setCurrentJobId(response.job_id)
        const qid = response.question_id
        if (qid) {
          // Navigate immediately - forecast is already running in background
          navigate(`/results/${qid}`)
        } else {
          // If no question_id yet, wait a bit for it to be created
          setTimeout(() => {
            if (response.job_id) {
              navigate(`/results`)
            }
          }, 500)
        }
      }
    } catch (error) {
      console.error('Failed to start forecast:', error)
      alert('Failed to start forecast: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex justify-center px-4 sm:px-6 py-8 sm:py-10">
      <div className="w-full max-w-3xl">
        <h1 className="text-center font-serif font-semibold tracking-tight text-2xl sm:text-4xl text-muted mb-6 sm:mb-8">
          Forecast <span className="text-green">anything</span>
        </h1>

        <div className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
          <label className="block text-xs font-semibold text-muted tracking-wider mb-2">Question</label>
          <div className="relative">
            <textarea
              className={[
                'w-full',
                'resize-y',
                'rounded-xl',
                'border',
                'border-rule',
                'bg-paper',
                'text-ink',
                'p-3',
                'sm:p-4',
                'text-sm',
                'sm:text-base',
                'leading-relaxed',
                'shadow-[inset_0_1px_0_rgba(20,18,16,.05)]',
                'focus:outline-none',
                'focus:ring-2',
                'focus:ring-green/30',
                'focus:border-green/60',
                'min-h-[100px]',
              ].join(' ')}
              placeholder={
                question.trim() === '' && !isFocused && formatterStatus !== 'reformatting' && typewriterText
                  ? ''
                  : 'Ask a question about the future…'
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              rows={3}
              disabled={formatterStatus === 'reformatting'}
            />
            {question.trim() === '' && !isFocused && formatterStatus !== 'reformatting' && (
              <div className="pointer-events-none absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 text-muted text-sm sm:text-base leading-relaxed">
                {typewriterText}
                <span className="ml-0.5 inline-block animate-pulse">|</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setShowCustomQuestion((v) => !v)}
                aria-expanded={showCustomQuestion}
                className={[
                  'rounded-xl',
                  'px-3',
                  'py-1.5',
                  'border',
                  'border-rule',
                  'bg-paper',
                  'text-ink/80',
                  'font-semibold',
                  'text-xs',
                  'shadow-delphi',
                  'hover:bg-white/40',
                  'hover:text-ink',
                  'transition',
                  'text-left',
                ].join(' ')}
              >
                {showCustomQuestion ? 'Hide' : 'Specify your own question'}
              </button>
              <button
                type="button"
                onClick={() => setShowSettings((v) => !v)}
                aria-expanded={showSettings}
                className={[
                  'rounded-xl',
                  'px-3',
                  'py-1.5',
                  'border',
                  'border-rule',
                  'bg-paper',
                  'text-ink/80',
                  'font-semibold',
                  'text-xs',
                  'shadow-delphi',
                  'hover:bg-white/40',
                  'hover:text-ink',
                  'transition',
                  'text-left',
                ].join(' ')}
              >
                {showSettings ? 'Hide' : 'Show'} forecast settings
              </button>
            </div>

            {formatterStatus === 'idle' && (
              <button
                className={[
                  'rounded-xl',
                  'px-4',
                  'py-2',
                  'bg-green',
                  'text-white',
                  'font-semibold',
                  'text-sm',
                  'shadow-delphi',
                  'hover:brightness-[0.98]',
                  'disabled:opacity-60',
                  'disabled:cursor-not-allowed',
                  'transition',
                ].join(' ')}
                onClick={() => void handleStartForecast()}
                disabled={isLoading}
              >
                ▶ Start forecast
              </button>
            )}
          </div>
        </div>

        {showCustomQuestion && (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="text-lg sm:text-xl font-semibold text-ink">
                  Specify your own question
                </div>
                <Tooltip content="A good forecasting question is specific, measurable, and has a clear resolution date. It should be binary (yes/no) or have quantifiable outcomes. Avoid vague or subjective questions. Well-specified questions lead to more accurate predictions and easier resolution.">
                  <span className="text-xs text-muted cursor-help">ℹ</span>
                </Tooltip>
              </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted tracking-wider mb-2">Question</label>
                <textarea
                  className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60 min-h-[80px]"
                  value={customQuestion}
                  onChange={(e) => setCustomQuestion(e.target.value)}
                  placeholder="Enter your forecasting question..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted tracking-wider mb-2">Resolution criteria</label>
                <textarea
                  className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60 min-h-[60px]"
                  value={customCriteria}
                  onChange={(e) => setCustomCriteria(e.target.value)}
                  placeholder="How will this question be resolved?"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted tracking-wider mb-2">Resolution date (optional)</label>
                <input
                  type="date"
                  className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
              <button
                className="rounded-xl px-4 py-2.5 bg-green text-white font-semibold text-sm shadow-delphi hover:brightness-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition"
                type="button"
                onClick={() => void handleStartCustomForecast()}
                disabled={isLoading || !customQuestion.trim() || !customCriteria.trim()}
              >
                ▶ Start forecast
              </button>
            </div>
          </div>
        )}

        {showSettings && (
          <div className="mt-4 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
            <ForecastSettings config={config} onChange={setConfig} />
          </div>
        )}

        {(formatterStatus === 'reformatting' || formatterStatus === 'ready' || formatterStatus === 'error') && (
          <div className="mt-6 rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg sm:text-xl font-semibold text-ink">
                {formatterStatus === 'reformatting'
                  ? 'Reformatting your question'
                  : formatterStatus === 'ready'
                    ? 'Review and confirm'
                    : 'Unable to reformat'}
              </div>
              <button className="text-xs text-muted hover:text-ink transition" type="button" onClick={handleEditQuestion}>
                Edit question
              </button>
            </div>

            {formatterStatus === 'reformatting' && (
              <>
                <div className="mt-1 text-sm text-muted">
                  Turning your input into a well-specified forecasting question.
                </div>
                <div className="mt-3 h-2 rounded-full bg-black/10 overflow-hidden">
                  <div className="h-full w-1/3 bg-green/60 animate-pulse" />
                </div>
                {formatterSearch.status === 'searching' && formatterSearch.query && (
                  <div className="mt-2 text-xs text-muted">
                    Validating with web search: “{formatterSearch.query}”
                  </div>
                )}
                {formatterSearch.status === 'complete' && formatterSearch.query && (
                  <div className="mt-2 text-xs text-muted">
                    Validation search complete ({formatterSearch.num_results ?? 0} results).
                  </div>
                )}
              </>
            )}

            {formatterStatus === 'ready' && (
              <>
                {formatted.is_valid === false ? (
                  <div className="mt-3 rounded-xl border border-wine/30 bg-wine/10 p-3">
                    <div className="font-semibold text-wine">This question cannot be forecast</div>
                    <div className="mt-1 text-sm text-wine/90">
                      {formatted.error_message || formatted.validation_message || 'Please adjust the question and try again.'}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-muted tracking-wider mb-2">Formatted question</div>
                      <textarea
                        className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm sm:text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60 min-h-[60px]"
                        value={editableFormatted.question || formatted.binary_question}
                        onChange={(e) => setEditableFormatted((prev) => ({ ...prev, question: e.target.value }))}
                        placeholder="Enter your forecasting question..."
                      />
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-muted tracking-wider mb-2">Resolution date</div>
                      <input
                        type="date"
                        className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60"
                        value={editableFormatted.endDate || formatted.end_date || ''}
                        onChange={(e) => setEditableFormatted((prev) => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-muted tracking-wider mb-2">Resolution criteria</div>
                      <textarea
                        className="w-full rounded-xl border border-rule bg-paper text-ink p-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-green/30 focus:border-green/60 min-h-[60px]"
                        value={editableFormatted.criteria || formatted.resolution_criteria || ''}
                        onChange={(e) => setEditableFormatted((prev) => ({ ...prev, criteria: e.target.value }))}
                        placeholder="How will this question be resolved?"
                      />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        className={[
                          'rounded-xl',
                          'px-4',
                          'py-2.5',
                          'bg-green',
                          'text-white',
                          'font-semibold',
                          'text-sm',
                          'shadow-delphi',
                          'hover:brightness-[0.98]',
                          'disabled:opacity-60',
                          'disabled:cursor-not-allowed',
                          'transition',
                        ].join(' ')}
                        type="button"
                        onClick={() => void handleRunForecastWithEdits()}
                        disabled={isLoading || !editableFormatted.question.trim()}
                      >
                        Run forecast
                      </button>
                      <button
                        className={[
                          'rounded-xl',
                          'px-4',
                          'py-2.5',
                          'border',
                          'border-rule',
                          'bg-paper',
                          'text-ink',
                          'font-semibold',
                          'text-sm',
                          'shadow-delphi',
                          'hover:bg-white/40',
                          'disabled:opacity-60',
                          'disabled:cursor-not-allowed',
                          'transition',
                        ].join(' ')}
                        type="button"
                        onClick={handleEditQuestion}
                        disabled={isLoading}
                      >
                        Change question
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="font-semibold text-lg sm:text-xl text-ink">Suggested questions</h2>
            <button
              type="button"
              onClick={() => navigate('/history')}
              className="rounded-xl border border-rule bg-paper shadow-delphi px-3 py-2 text-sm font-semibold text-ink hover:bg-white/40 transition"
            >
              See previous forecasts →
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTED_CATEGORIES.map((c) => {
              const active = c.key === activeCategory
              return (
                <button
                  key={c.key}
                  type="button"
                  className={[
                    'rounded-full',
                    'border',
                    active ? 'border-green/40 bg-green/10 text-ink' : 'border-rule bg-paper2 text-muted',
                    'shadow-delphi',
                    'px-3',
                    'py-1.5',
                    'text-[11px]',
                    'font-semibold',
                    'tracking-wider',
                    'hover:bg-white/30',
                    'transition',
                  ].join(' ')}
                  onClick={() => setActiveCategory(c.key)}
                >
                  {c.label}
                </button>
              )
            })}
          </div>

          <div className="flex flex-col gap-2">
            {(SUGGESTED_CATEGORIES.find((c) => c.key === activeCategory)?.questions || []).map((q, i) => (
              <button
                key={`${activeCategory}-${i}`}
                className={[
                  'text-left',
                  'rounded-delphi',
                  'border',
                  'border-rule',
                  'bg-paper2',
                  'shadow-delphi',
                  'px-4',
                  'py-3',
                  'text-sm',
                  'sm:text-base',
                  'text-ink',
                  'hover:bg-white/30',
                  'hover:border-[#b8924a]',
                  'transition-all',
                  'duration-200',
                  'flex',
                  'items-start',
                  'justify-between',
                  'gap-3',
                  'group',
                  'disabled:opacity-60',
                  'disabled:cursor-not-allowed',
                ].join(' ')}
                type="button"
                onClick={() => handleSuggestedQuestionPick(q)}
                disabled={isLoading || formatterStatus === 'reformatting'}
              >
                <span className="leading-relaxed group-hover:text-[#b8924a] transition-colors">{q}</span>
                <span className="text-muted text-xl leading-none group-hover:text-[#b8924a] transition-colors">›</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
